import {
  Card,
  CardContent,
  Badge,
  Button,
  cn,
  CLIENT_LOCALE,
  useFocusStore,
  navigateToThread,
  getCurrentFocus,
  navigateWithThreadPreservation,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@yeshuman/sdk'
import { Observation } from '../hooks/use-observations'
import { Edit, Trash2, Eye, ExternalLink, Stethoscope, Activity } from 'lucide-react'
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom'

interface ObservationCardProps {
  observation: Observation
  onEdit?: (observation: Observation) => void
  onDelete?: (observationId: string) => void
  isHighlighted?: boolean  // For agent update animation
}

export function ObservationCard({ observation, onEdit, onDelete, isHighlighted }: ObservationCardProps) {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const location = useLocation()
  const { focus } = useFocusStore()

  const handleThreadClick = () => {
    if (observation.thread_id) {
      // If we're on /observations, stay on that route and just update the thread parameter
      if (location.pathname === '/observations') {
        const params = new URLSearchParams(searchParams)
        params.set('thread', observation.thread_id)
        navigate(`/observations?${params.toString()}`)
      } else {
        // Otherwise, navigate to the focus route with thread parameter
        const currentFocus = getCurrentFocus(focus, 'patient')
        navigateToThread(navigate, observation.thread_id, currentFocus, true, searchParams)
      }
    }
  }

  const handleCorrelationClick = (rel: Observation['relations'][0]) => {
    if (rel.content_type === 'symptom' && rel.content_object?.id) {
      // Navigate to symptoms page
      navigateWithThreadPreservation(navigate, '/symptoms', searchParams)
    } else if (rel.content_type === 'activity' && rel.content_object?.id) {
      // Navigate to activity page
      navigateWithThreadPreservation(navigate, '/activity', searchParams)
    }
  }

  const getRelationTypeLabel = (relationType: string) => {
    const labels: Record<string, string> = {
      'related_to': 'Related to',
      'context_for': 'Context for',
      'follows': 'Follows',
      'contradicts': 'Contradicts',
      'confirms': 'Confirms',
    }
    return labels[relationType] || relationType
  }

  const formatDateTime = (dateTimeStr: string) => {
    const date = new Date(dateTimeStr)
    const today = new Date()
    const yesterday = new Date(today)
    yesterday.setDate(yesterday.getDate() - 1)
    
    const dateOnly = new Date(date)
    dateOnly.setHours(0, 0, 0, 0)
    const todayOnly = new Date(today)
    todayOnly.setHours(0, 0, 0, 0)
    const yesterdayOnly = new Date(yesterday)
    yesterdayOnly.setHours(0, 0, 0, 0)
    
    if (dateOnly.getTime() === todayOnly.getTime()) {
      return `Today at ${date.toLocaleTimeString(CLIENT_LOCALE, { hour: 'numeric', minute: '2-digit', hour12: true })}`
    } else if (dateOnly.getTime() === yesterdayOnly.getTime()) {
      return `Yesterday at ${date.toLocaleTimeString(CLIENT_LOCALE, { hour: 'numeric', minute: '2-digit', hour12: true })}`
    } else {
      return date.toLocaleString(CLIENT_LOCALE, { 
        month: 'short', 
        day: 'numeric', 
        year: 'numeric',
        hour: 'numeric', 
        minute: '2-digit',
        hour12: true 
      })
    }
  }

  return (
    <Card
      className={cn(
        "transition-all duration-300 hover:shadow-md",
        isHighlighted && "ring-2 ring-primary ring-offset-2 animate-pulse"
      )}
    >
      <CardContent>
        <div className="flex items-start justify-between">
          <div className="flex-1 space-y-2">
            <div className="flex items-center gap-2 flex-wrap">
              <Badge variant="outline" className="flex items-center gap-1">
                <Eye className="h-3.5 w-3.5" />
                Observation
              </Badge>
              <span className="text-sm text-muted-foreground">{formatDateTime(observation.created_at)}</span>
              {observation.thread_id && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-6 px-2 text-xs"
                  onClick={handleThreadClick}
                >
                  <ExternalLink className="h-3.5 w-3.5 mr-1" />
                  Thread
                </Button>
              )}
            </div>
            
            <div>
              <p className="text-sm leading-relaxed">{observation.text}</p>
              
              {/* Source Message Quote */}
              {observation.source_message_text && (
                <div className="mt-2 rounded-md border border-border/60 bg-muted/40 p-4">
                  <blockquote className="m-0 border-0 p-0 text-xs text-muted-foreground italic pl-0">
                    {observation.source_message_text}
                  </blockquote>
                </div>
              )}
              
              {/* Show correlations as badges */}
              {observation.relations && observation.relations.length > 0 && (
                <div className="flex gap-2 mt-2 flex-wrap">
                  <TooltipProvider>
                    {observation.relations.map((rel) => {
                      const isClickable = !!(rel.content_object?.id)
                      const relationTypeLabel = rel.relation_type !== 'related_to' 
                        ? `${getRelationTypeLabel(rel.relation_type)}: ` 
                        : ''
                      
                      const tooltipContent = (
                        <div className="space-y-1 text-xs">
                          <div className="font-semibold">{getRelationTypeLabel(rel.relation_type)}</div>
                          {rel.content_type === 'symptom' && rel.content_object?.severity && (
                            <div>Severity: {rel.content_object.severity}</div>
                          )}
                          {rel.content_object?.occurred_at && (
                            <div>Date: {new Date(rel.content_object.occurred_at).toLocaleDateString()}</div>
                          )}
                          {rel.content_object?.date && (
                            <div>Date: {new Date(rel.content_object.date).toLocaleDateString()}</div>
                          )}
                          <div className="text-muted-foreground">
                            {rel.discovery_method === 'agent' ? 'Auto-detected' : 'Manually linked'}
                          </div>
                        </div>
                      )

                      return (
                        <Tooltip key={rel.id}>
                          <TooltipTrigger asChild>
                            <Badge 
                              variant="secondary" 
                              className={cn(
                                "text-xs flex items-center gap-1",
                                isClickable && "cursor-pointer hover:bg-secondary/80 transition-colors"
                              )}
                              onClick={() => isClickable && handleCorrelationClick(rel)}
                            >
                              {rel.content_type === 'symptom' ? (
                                <Stethoscope className="h-3.5 w-3.5" />
                              ) : (
                                <Activity className="h-3.5 w-3.5" />
                              )}
                              {relationTypeLabel}
                              {rel.content_type === 'symptom' ? (
                                <>
                                  {rel.content_object?.symptom_type || 'Unknown'}
                                  {rel.content_object?.severity && (
                                    <span className="ml-1 opacity-70">({rel.content_object.severity})</span>
                                  )}
                                </>
                              ) : (
                                <>
                                  {rel.content_object?.activity_type || 'Unknown'}
                                </>
                              )}
                              {rel.discovery_method === 'agent' && (
                                <span className="ml-1 opacity-70">(auto)</span>
                              )}
                            </Badge>
                          </TooltipTrigger>
                          <TooltipContent>
                            {tooltipContent}
                          </TooltipContent>
                        </Tooltip>
                      )
                    })}
                  </TooltipProvider>
                </div>
              )}
            </div>
          </div>
          
          {(onEdit || onDelete) && (
            <div className="flex gap-1 ml-4">
              {onEdit && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => onEdit(observation)}
                  className="h-8 w-8 p-0"
                >
                  <Edit className="h-4 w-4" />
                </Button>
              )}
              {onDelete && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => onDelete(observation.id)}
                  className="h-8 w-8 p-0 text-destructive hover:text-destructive"
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              )}
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  )
}

